import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { CheckoutButton, CheckoutCardHeader } from './checkoutStyle';
import { getMaxCreditsCanBeUsed } from '../../helpers/costCounter';
import {
  clearPaymentWithCredits,
  IShoppingCartItemWithService,
  payWithCredits,
} from '../../redux/reducers/shoppingCartReducer';
import { IsSewingCompany } from '../../helpers/clientConfigs';
import { roundDoubleNumbers } from '../../helpers/currency';

interface CreditsProps {
  shoppingCartItems: IShoppingCartItemWithService[];
}

export function Credits({ shoppingCartItems }: CreditsProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const usedCredits = useAppSelector((state: RootState) => state.shoppingCartState.usedCredits);
  const deliveryPrice = useAppSelector((state: RootState) => state.shoppingCartState.deliveryPrice);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const couponDiscount = useAppSelector((state: RootState) => state.shoppingCartState.couponDiscount);

  const [allServicesCanBePaidWithCredits, setAllServicesCanBePaidWithCredits] = useState<boolean>(true);
  const [isCreditsUsed, setIsCreditsUsed] = useState<boolean>(false);
  const [maxCreditsToUse, setMaxCreditsToUse] = useState<number>(0);

  useEffect(() => {
    var maxCredits = getMaxCreditsCanBeUsed();
    setMaxCreditsToUse(maxCredits);
  }, [shoppingCartItems, usedCredits, totalPrice, couponDiscount]);

  useEffect(() => {
    setAllServicesCanBePaidWithCredits(canAllItemsBePaidWithCredits());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoppingCartItems]);

  useEffect(() => {
    if (!isCreditsUsed) {
      if (usedCredits > 0) {
        dispatch(clearPaymentWithCredits());
      }
      return;
    }

    if (maxCreditsToUse < usedCredits) {
      dispatch(payWithCredits(maxCreditsToUse));
    }
  }, [maxCreditsToUse, isCreditsUsed, dispatch, usedCredits]);

  const handleUseCreditsClick = () => {
    dispatch(payWithCredits(maxCreditsToUse));
    setIsCreditsUsed(true);
  };

  const handleCancelCreditsClick = () => {
    setIsCreditsUsed(false);
  };

  const canAllItemsBePaidWithCredits = () => {
    if (deliveryPrice > 0) {
      return false;
    }

    if (shoppingCartItems?.some((item) => !item.serviceItem.canBePaidWithCredits)) {
      return false;
    }

    return true;
  };

  return (
    <Box>
      <CheckoutCardHeader>{t('payment:creditsHeader')}</CheckoutCardHeader>
      {!isCreditsUsed && (
        <>
          {maxCreditsToUse > 0 && (
            <Grid container direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
              <Grid size="grow">
                <Typography variant="body2">
                  {t('payment:useCreditsForOrderPart1')}
                  <Box component="span" fontWeight="bold" color="secondary.main" sx={{ whiteSpace: 'nowrap' }}>
                    {roundDoubleNumbers(maxCreditsToUse) + ' €'}
                  </Box>
                  {t('payment:useCreditsForOrderPart2')}
                </Typography>
              </Grid>
              <Grid size="auto">
                <CheckoutButton variant="contained" onClick={handleUseCreditsClick}>
                  {t('order:apply')}
                </CheckoutButton>
              </Grid>
            </Grid>
          )}
          {!allServicesCanBePaidWithCredits && (
            <>
              <Typography sx={{ color: 'error.main', fontSize: '13px' }}>
                {IsSewingCompany ? t('payment:creditsCanBeUsedForSewing') : t('payment:creditsCanBeUsedForLaundry')}
              </Typography>
              <Typography sx={{ color: 'error.main', fontSize: '13px' }}>{t('payment:creditsNowALlowed')}</Typography>
            </>
          )}
        </>
      )}
      {isCreditsUsed && (
        <>
          <Grid container direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Grid size="grow">
              <Typography variant="body2">
                <Box component="span" fontWeight="bold" color="secondary.main">
                  {roundDoubleNumbers(usedCredits)} €
                </Box>
                {t('payment:appliedCredits')}
              </Typography>
            </Grid>
            <Grid size="auto">
              <CheckoutButton variant="contained" onClick={handleCancelCreditsClick}>
                {t('order:cancel')}
              </CheckoutButton>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
